<template>
  <Teleport to="body">
    <Transition name="rs-fade">
      <div v-if="open" class="rs" @click.self="close">
        <div class="rs__sheet" dir="rtl">

          <div class="rs__head">
            <h3 class="rs__title">
              <i class="fa fa-flag"></i>
              الإبلاغ عن محتوى
            </h3>
            <button class="rs__close" @click="close" type="button" aria-label="إغلاق">
              <i class="fa fa-times"></i>
            </button>
          </div>

          <!-- Result state (success or "already reported") -->
          <div v-if="doneMessage" class="rs__done">
            <i class="fa fa-check-circle"></i>
            <p>{{ doneMessage }}</p>
            <button class="rs__done-btn" @click="close" type="button">إغلاق</button>
          </div>

          <!-- Form state -->
          <template v-else>
            <p class="rs__sub">ما سبب البلاغ؟</p>

            <div class="rs__reasons">
              <button
                v-for="r in REASONS"
                :key="r.code"
                class="rs__reason"
                :class="{ 'rs__reason--on': reason === r.code }"
                @click="reason = r.code"
                type="button"
              >
                <span class="rs__reason-radio"></span>
                {{ r.label }}
              </button>
            </div>

            <textarea
              v-model.trim="details"
              class="rs__details"
              rows="3"
              maxlength="1000"
              placeholder="تفاصيل إضافية (اختياري)"
            ></textarea>

            <p v-if="error" class="rs__error">
              <i class="fa fa-exclamation-circle"></i>
              {{ error }}
            </p>

            <button
              class="rs__submit"
              :disabled="!reason || submitting"
              @click="submit"
              type="button"
            >
              <i class="fa" :class="submitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
              {{ submitting ? "جاري الإرسال..." : "إرسال البلاغ" }}
            </button>
          </template>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { api } from "@/services/api";

const props = defineProps({
  open:       { type: Boolean, required: true },
  targetType: { type: String,  required: true }, // "branch" | "review" | "menu_vote"
  targetId:   { type: [Number, String], default: null },
});

const emit   = defineEmits(["close", "gone"]);
const router = useRouter();

const REASONS = [
  { code: "spam",           label: "محتوى مزعج / دعائي" },
  { code: "offensive",      label: "محتوى مسيء" },
  { code: "wrong_info",     label: "معلومات خاطئة" },
  { code: "not_this_place", label: "لا يخص هذا المكان" },
  { code: "other",          label: "أخرى" },
];

const reason      = ref("");
const details     = ref("");
const submitting  = ref(false);
const error       = ref("");
const doneMessage = ref("");

// Fresh form every time the sheet opens
watch(() => props.open, (v) => {
  if (v) {
    reason.value      = "";
    details.value     = "";
    error.value       = "";
    doneMessage.value = "";
    submitting.value  = false;
  }
});

function close() {
  emit("close");
}

async function submit() {
  if (!reason.value || submitting.value) return;
  if (!localStorage.getItem("token")) {
    emit("close");
    router.push({ name: "Account" });
    return;
  }

  submitting.value = true;
  error.value      = "";
  try {
    const payload = {
      target_type: props.targetType,
      target_id:   props.targetId,
      reason_code: reason.value,
    };
    if (details.value) payload.details = details.value;

    const res = await api.post("/reports", payload);
    doneMessage.value = res.data?.message || "شكراً — تم استلام البلاغ";
  } catch (e) {
    const status = e?.response?.status;
    if (status === 409) {
      // Already reported — show the server message as the final state
      doneMessage.value = e.response.data?.message || "سبق أن أبلغت عن هذا المحتوى";
    } else if (status === 401) {
      emit("close");
      router.push({ name: "Account" });
    } else if (status === 404) {
      doneMessage.value = "هذا المحتوى لم يعد موجوداً";
      emit("gone");
    } else if (status === 422 && e.response.data?.errors) {
      error.value = Object.values(e.response.data.errors).flat().join(" — ");
    } else {
      error.value = e?.response?.data?.message || "تعذر إرسال البلاغ، حاول مجدداً";
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<style scoped lang="scss">
.rs {
  position: fixed; inset: 0;
  z-index: 10000;
  background: rgba(0, 0, 0, 0.55);
  display: flex; align-items: flex-end; justify-content: center;

  &__sheet {
    width: 100%; max-width: 560px;
    background: var(--color-panel);
    border: 1px solid var(--color-border);
    border-bottom: none;
    border-radius: var(--radius-xl) var(--radius-xl) 0 0;
    padding: var(--space-4) var(--page-padding-x) max(var(--space-4), env(safe-area-inset-bottom, 16px));
    display: flex; flex-direction: column; gap: var(--space-3);
  }

  &__head {
    display: flex; align-items: center; justify-content: space-between;
  }

  &__title {
    margin: 0;
    display: flex; align-items: center; gap: var(--space-2);
    font-size: 0.98rem; font-weight: 800; color: var(--color-text-1);
    i { color: var(--color-closed, #D86545); font-size: 0.85rem; }
  }

  &__close {
    width: 32px; height: 32px; border-radius: 50%;
    border: 1px solid var(--color-border); background: var(--color-card);
    color: var(--color-text-3); cursor: pointer;
    display: grid; place-items: center; font-size: 0.8rem;
    &:hover { color: var(--color-text-1); }
  }

  &__sub {
    margin: 0; font-size: 0.82rem; color: var(--color-text-3);
  }

  &__reasons {
    display: flex; flex-direction: column; gap: var(--space-1);
  }

  &__reason {
    display: flex; align-items: center; gap: var(--space-2);
    padding: 11px var(--space-3);
    border-radius: var(--radius-md);
    background: var(--color-card); border: 1px solid var(--color-border);
    color: var(--color-text-2); font-family: var(--font-primary);
    font-size: 0.85rem; font-weight: 600; cursor: pointer;
    text-align: right;
    transition: all var(--transition-base);

    &:hover { border-color: var(--color-border-lit); }

    &--on {
      border-color: var(--color-signal);
      background: var(--color-signal-soft);
      color: var(--color-text-1);

      .rs__reason-radio {
        border-color: var(--color-signal);
        &::after { opacity: 1; }
      }
    }
  }

  &__reason-radio {
    width: 16px; height: 16px; border-radius: 50%;
    border: 2px solid var(--color-border-lit);
    flex-shrink: 0;
    display: grid; place-items: center;
    &::after {
      content: "";
      width: 8px; height: 8px; border-radius: 50%;
      background: var(--color-signal);
      opacity: 0;
      transition: opacity 0.12s;
    }
  }

  &__details {
    background: var(--color-card); border: 1px solid var(--color-border-lit);
    border-radius: var(--radius-md); padding: 10px var(--space-3);
    color: var(--color-text-1); font-family: var(--font-primary); font-size: 0.85rem;
    resize: vertical; min-height: 64px; width: 100%; box-sizing: border-box;
    &:focus { outline: none; border-color: var(--color-signal); }
    &::placeholder { color: var(--color-text-4); }
  }

  &__error {
    margin: 0;
    display: flex; align-items: center; gap: var(--space-2);
    font-size: 0.78rem; color: var(--color-closed);
  }

  &__submit {
    display: flex; align-items: center; justify-content: center; gap: var(--space-2);
    width: 100%; padding: 13px;
    border-radius: var(--radius-md); border: none;
    background: var(--color-closed, #D86545); color: #fff;
    font-family: var(--font-primary); font-size: 0.9rem; font-weight: 700;
    cursor: pointer; transition: opacity var(--transition-base);
    &:hover:not(:disabled) { opacity: 0.9; }
    &:disabled {
      background: var(--color-card); color: var(--color-text-4);
      border: 1px solid var(--color-border); cursor: not-allowed;
    }
  }

  &__done {
    display: flex; flex-direction: column; align-items: center; gap: var(--space-2);
    padding: var(--space-4) 0;
    text-align: center;

    i { font-size: 2rem; color: var(--color-open, #8FA37A); }
    p { margin: 0; font-size: 0.9rem; color: var(--color-text-1); line-height: 1.6; }
  }

  &__done-btn {
    margin-top: var(--space-2);
    padding: 10px 32px; border-radius: var(--radius-md);
    border: 1px solid var(--color-border-lit); background: var(--color-card);
    color: var(--color-text-1); font-family: var(--font-primary);
    font-size: 0.85rem; font-weight: 600; cursor: pointer;
    &:hover { border-color: var(--color-signal); color: var(--color-signal); }
  }
}

.rs-fade-enter-active,
.rs-fade-leave-active {
  transition: opacity 0.2s ease;
  .rs__sheet { transition: transform 0.22s ease; }
}
.rs-fade-enter-from,
.rs-fade-leave-to {
  opacity: 0;
  .rs__sheet { transform: translateY(40px); }
}
</style>
